import React, { useState, useEffect, useCallback } from "react";
import { Card, Typography, Flex, Tag, Input, InputNumber, Button, Row, Col, Tooltip, Popconfirm, message, Spin, Switch, Divider } from "antd";
import { PlusOutlined, SaveOutlined, TagsOutlined, PhoneOutlined, MailOutlined, GlobalOutlined, DeleteOutlined, CoffeeOutlined, AlertOutlined } from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { setCallStatus } from "../../redux/slices/executorSlice";
import useAuthSession from "../../hooks/useAuthSession";
import { getCompanySettings, updateCompanySettings } from "../../services/organizationService";

const { Title, Text } = Typography;

const normalizeCollabs = (list) => {
  if (!Array.isArray(list)) return null;
  return list.map((c) => (typeof c === "string" ? { companyCode: c, companyName: c, status: "active" } : c));
};

const applySettings = (s, setters, dispatch) => {
  if (!s) return;
  const {
    setTags,
    setAutoBreakEnabled,
    setInactivityTimeoutMinutes,
    setAlertAdminOnBreakExceeded,
    setBreakHourLimit,
    setConnectedCallDuration,
    setDailyConnectedCallTarget,
    setResendApiKey,
    setResendSenderDomain,
    setCollabs,
    setLeadStatuses,
    setInterestedStatuses,
    setDnpStatuses,
    setConvertedStatuses,
    setRemarks,
  } = setters;

  if (Array.isArray(s.tags)) setTags(s.tags);
  if (s.autoBreakEnabled !== undefined) setAutoBreakEnabled(Boolean(s.autoBreakEnabled));
  if (s.inactivityTimeoutMinutes !== undefined) setInactivityTimeoutMinutes(Number(s.inactivityTimeoutMinutes) || 5);
  if (s.alertAdminOnBreakExceeded !== undefined) setAlertAdminOnBreakExceeded(Boolean(s.alertAdminOnBreakExceeded));
  if (s.breakHourLimit !== undefined) setBreakHourLimit(Number(s.breakHourLimit) || 60);
  if (s.connectedCallDuration !== undefined) setConnectedCallDuration(Number(s.connectedCallDuration) || 0);
  if (s.dailyConnectedCallTarget !== undefined) setDailyConnectedCallTarget(Number(s.dailyConnectedCallTarget) || 50);
  if (s.resendApiKey) setResendApiKey(s.resendApiKey);
  if (s.resendSenderDomain) setResendSenderDomain(s.resendSenderDomain);

  const normalizedCollabs = normalizeCollabs(s.collaboratingCompanies);
  if (normalizedCollabs) setCollabs(normalizedCollabs);

  if (Array.isArray(s.leadStatuses) && s.leadStatuses.length > 0) {
    setLeadStatuses(s.leadStatuses);
    dispatch(setCallStatus(s.leadStatuses));
  }
  if (Array.isArray(s.interestedPageStatuses)) setInterestedStatuses(s.interestedPageStatuses);
  if (Array.isArray(s.dnpPageStatuses)) setDnpStatuses(s.dnpPageStatuses);
  if (Array.isArray(s.convertedPageStatuses)) setConvertedStatuses(s.convertedPageStatuses);
  if (Array.isArray(s.productRemarks)) setRemarks(s.productRemarks);
};

const EMPTY_ARRAY = [];

function AppSettings() {
  const dispatch = useDispatch();
  const session = useAuthSession();
  const reduxCompanyCode = useSelector((state) => state.executor?.companyInfo?.companyCode);
  const reduxCallStatus = useSelector((state) => state.executor?.companyInfo?.callStatus);
  const reduxCompanyTags = useSelector((state) => state.executor?.companyInfo?.tags || state.session?.settings?.tags) || EMPTY_ARRAY;
  const effectiveCompanyCode = session.companyCode || reduxCompanyCode || "";

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Tags State
  const [tags, setTags] = useState(() => (Array.isArray(reduxCompanyTags) && reduxCompanyTags.length > 0 ? reduxCompanyTags : []));
  const [newTagInput, setNewTagInput] = useState("");

  // Call & Break Settings
  const [autoBreakEnabled, setAutoBreakEnabled] = useState(true);
  const [inactivityTimeoutMinutes, setInactivityTimeoutMinutes] = useState(5);
  const [alertAdminOnBreakExceeded, setAlertAdminOnBreakExceeded] = useState(true);
  const [breakHourLimit, setBreakHourLimit] = useState(60);
  const [connectedCallDuration, setConnectedCallDuration] = useState(10);
  const [dailyConnectedCallTarget, setDailyConnectedCallTarget] = useState(50);

  // Email Integration
  const [resendApiKey, setResendApiKey] = useState("");
  const [resendSenderDomain, setResendSenderDomain] = useState("");

  // Collaborating Companies
  const [collabs, setCollabs] = useState([]);
  const [newCollabInput, setNewCollabInput] = useState("");

  // Lead Statuses & Filter Matrix
  const [leadStatuses, setLeadStatuses] = useState(() => (Array.isArray(reduxCallStatus) && reduxCallStatus.length > 0 ? reduxCallStatus : ["New", "Converted", "Follow Up", "Not Interested", "Connected", "Invalid", "Not Connected"]).filter((st) => st && st.toLowerCase() !== "closed lost"));
  const [interestedStatuses, setInterestedStatuses] = useState(["Follow Up"]);
  const [dnpStatuses, setDnpStatuses] = useState(["Not Connected", "Not Interested", "Invalid"]);
  const [convertedStatuses, setConvertedStatuses] = useState(["Converted"]);

  // Product Remarks
  const [remarks, setRemarks] = useState([]);

  const loadSettings = useCallback(async () => {
    if (!effectiveCompanyCode) return;
    setLoading(true);
    try {
      const res = await getCompanySettings(effectiveCompanyCode);
      if (res?.success && res.settings) {
        applySettings(
          res.settings,
          {
            setTags,
            setAutoBreakEnabled,
            setInactivityTimeoutMinutes,
            setAlertAdminOnBreakExceeded,
            setBreakHourLimit,
            setConnectedCallDuration,
            setDailyConnectedCallTarget,
            setResendApiKey,
            setResendSenderDomain,
            setCollabs,
            setLeadStatuses,
            setInterestedStatuses,
            setDnpStatuses,
            setConvertedStatuses,
            setRemarks,
          },
          dispatch,
        );
      }
    } catch (err) {
      console.error("Failed to load app settings:", err);
    } finally {
      setLoading(false);
    }
  }, [effectiveCompanyCode, dispatch]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Tag Handlers
  const handleAddTag = async () => {
    const trimmed = newTagInput.trim();
    if (!trimmed) return;
    if (tags.includes(trimmed)) {
      message.warning("Tag already exists.");
      return;
    }
    const updatedTags = [...tags, trimmed];
    setTags(updatedTags);
    setNewTagInput("");
    try {
      await updateCompanySettings(effectiveCompanyCode, { tags: updatedTags });
      message.success(`Tag "${trimmed}" added successfully!`);
    } catch (err) {
      console.error("Failed to persist tag to DB:", err);
      message.success("Tag added successfully!");
    }
  };

  const handleRemoveTag = async (t) => {
    const updatedTags = tags.filter((tag) => tag !== t);
    setTags(updatedTags);
    try {
      await updateCompanySettings(effectiveCompanyCode, { tags: updatedTags });
      message.success(`Tag "${t}" removed successfully.`);
    } catch (err) {
      console.error("Failed to remove tag from DB:", err);
    }
  };

  // Collaborating Partners Handlers
  const handleAddCollab = async () => {
    const trimmed = newCollabInput.trim().toUpperCase();
    if (!trimmed) return;
    if (collabs.some((c) => (typeof c === "string" ? c : c.companyCode || c.companyName || "").toUpperCase() === trimmed)) {
      message.warning("Partner company / code already linked.");
      return;
    }
    const newEntry = { companyCode: trimmed, companyName: trimmed, status: "pending" };
    const updatedCollabs = [...collabs, newEntry];
    setCollabs(updatedCollabs);
    setNewCollabInput("");
    try {
      await updateCompanySettings(effectiveCompanyCode, { collaboratingCompanies: updatedCollabs });
      message.success(`Partner "${trimmed}" added successfully!`);
    } catch (err) {
      console.error("Failed to persist partner to DB:", err);
      message.success("Partner company added successfully!");
    }
  };

  const handleRemoveCollab = async (codeToRemove) => {
    const updatedCollabs = collabs.filter((c) => {
      const code = typeof c === "string" ? c : c.companyCode || c.companyName;
      return code !== codeToRemove;
    });
    setCollabs(updatedCollabs);
    try {
      await updateCompanySettings(effectiveCompanyCode, { collaboratingCompanies: updatedCollabs });
      message.success(`Partner "${codeToRemove}" removed successfully.`);
    } catch (err) {
      console.error("Failed to remove partner from DB:", err);
    }
  };

  const handleSaveAll = async () => {
    if (!effectiveCompanyCode) {
      message.error("Company code not found. Please refresh and try again.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        tags,
        autoBreakEnabled: Boolean(autoBreakEnabled),
        inactivityTimeoutMinutes: Number(inactivityTimeoutMinutes) || 5,
        alertAdminOnBreakExceeded: Boolean(alertAdminOnBreakExceeded),
        breakHourLimit: Number(breakHourLimit) || 60,
        connectedCallDuration: Number(connectedCallDuration) || 0,
        dailyConnectedCallTarget: Number(dailyConnectedCallTarget) || 50,
        resendApiKey,
        resendSenderDomain,
        collaboratingCompanies: collabs,
        leadStatuses,
        interestedPageStatuses: interestedStatuses,
        dnpPageStatuses: dnpStatuses,
        convertedPageStatuses: convertedStatuses,
        productRemarks: remarks,
      };

      const res = await updateCompanySettings(effectiveCompanyCode, payload);
      if (res?.success !== false) {
        dispatch(setCallStatus(leadStatuses));
        message.success("App settings saved successfully!");
      } else {
        message.error(res?.message || "Failed to save configurations.");
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to save app settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Spin spinning={loading}>
      <Flex vertical gap={20}>
        {/* Header Row */}
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <Flex vertical>
            <Title level={4} style={{ margin: 0 }}>
              System & Telemetry Settings
            </Title>
            <Text type="secondary" style={{ fontSize: "0.8rem" }}>
              Active Tenant: <strong style={{ color: "#0f172a" }}>{effectiveCompanyCode}</strong>
            </Text>
          </Flex>
          <Flex gap={10}>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSaveAll}>
              Save All Settings
            </Button>
          </Flex>
        </Flex>

        <Row gutter={[20, 20]}>
          {/* ── CARD 1: CALL TELEPHONY & AUTO BREAK INACTIVITY RULES ── */}
          <Col xs={24} lg={12}>
            <Card className="org-card" styles={{ body: { padding: 20 } }}>
              <Flex vertical gap={14}>
                <Flex align="center" justify="space-between">
                  <Flex align="center" gap={8}>
                    <PhoneOutlined style={{ color: "#2459d3", fontSize: 18 }} />
                    <Title level={5} style={{ margin: 0, fontSize: "0.95rem" }}>
                      Call Telephony & Break Rules
                    </Title>
                  </Flex>
                  <Button type="primary" size="small" icon={<SaveOutlined />} loading={saving} onClick={handleSaveAll}>
                    Save
                  </Button>
                </Flex>

                <div>
                  <Text style={{ fontSize: "0.85rem", fontWeight: 600 }}>Connected Call Threshold (Seconds)</Text>
                  <Text type="secondary" style={{ display: "block", fontSize: "0.75rem", marginBottom: 6 }}>
                    Calls exceeding this duration are counted as "Connected / Answered".
                  </Text>
                  <InputNumber min={0} max={300} placeholder="0" value={connectedCallDuration} onChange={(v) => setConnectedCallDuration(v)} style={{ width: "100%" }} />
                </div>

                <div>
                  <Text style={{ fontSize: "0.85rem", fontWeight: 600 }}>Daily Connected Call Target (Count)</Text>
                  <Text type="secondary" style={{ display: "block", fontSize: "0.75rem", marginBottom: 6 }}>
                    Daily connected calls target per sales representative (e.g. 50 calls).
                  </Text>
                  <InputNumber min={1} max={1000} placeholder="50" value={dailyConnectedCallTarget} onChange={(v) => setDailyConnectedCallTarget(v)} style={{ width: "100%" }} />
                </div>

                <Divider style={{ margin: "4px 0" }} />

                {/* Auto-Break on Inactivity Switch */}
                <Flex justify="space-between" align="center">
                  <Flex vertical style={{ maxWidth: "80%" }}>
                    <Text style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      <CoffeeOutlined style={{ color: "#f59e0b", marginRight: 6 }} /> Auto-Trigger Break on Inactivity
                    </Text>
                    <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                      Automatically pause sales rep and start break timer when user is inactive on the system.
                    </Text>
                  </Flex>
                  <Switch checked={autoBreakEnabled} onChange={(chk) => setAutoBreakEnabled(chk)} />
                </Flex>

                {/* Inactivity Threshold */}
                {autoBreakEnabled && (
                  <div>
                    <Text style={{ fontSize: "0.85rem", fontWeight: 600 }}>Inactivity Timeout Threshold (Minutes)</Text>
                    <Text type="secondary" style={{ display: "block", fontSize: "0.75rem", marginBottom: 6 }}>
                      Duration of no mouse/keyboard activity before auto break is activated.
                    </Text>
                    <InputNumber min={1} max={60} placeholder="5" value={inactivityTimeoutMinutes} onChange={(v) => setInactivityTimeoutMinutes(v)} style={{ width: "100%" }} />
                  </div>
                )}

                {/* Daily Maximum Break Limit */}
                <div>
                  <Text style={{ fontSize: "0.85rem", fontWeight: 600 }}>Daily Maximum Break Limit (Minutes)</Text>
                  <Text type="secondary" style={{ display: "block", fontSize: "0.75rem", marginBottom: 6 }}>
                    Alert admin if sales rep pause time exceeds this threshold.
                  </Text>
                  <InputNumber min={1} max={240} placeholder="60" value={breakHourLimit} onChange={(v) => setBreakHourLimit(v)} style={{ width: "100%" }} />
                </div>

                {/* Alert Admin Switch */}
                <Flex justify="space-between" align="center">
                  <Flex vertical style={{ maxWidth: "80%" }}>
                    <Text style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      <AlertOutlined style={{ color: "#e11d48", marginRight: 6 }} /> Alert Admin on Break Exceeded
                    </Text>
                    <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                      Send instant notification to company admins when a sales rep surpasses the daily limit.
                    </Text>
                  </Flex>
                  <Switch checked={alertAdminOnBreakExceeded} onChange={(chk) => setAlertAdminOnBreakExceeded(chk)} />
                </Flex>
              </Flex>
            </Card>
          </Col>

          {/* ── CARD 2: TAGS & LABELS ── */}
          <Col xs={24} lg={12}>
            <Card className="org-card" styles={{ body: { padding: 20 } }}>
              <Flex vertical gap={14}>
                <Flex align="center" gap={8}>
                  <TagsOutlined style={{ color: "#2459d3", fontSize: 18 }} />
                  <Title level={5} style={{ margin: 0, fontSize: "0.95rem" }}>
                    Company Tags & Labels ({tags.length})
                  </Title>
                </Flex>

                <Flex wrap="wrap" gap={8}>
                  {tags.map((t) => (
                    <Tag key={t} closable onClose={() => handleRemoveTag(t)} style={{ padding: "4px 10px", fontSize: "0.82rem", borderRadius: 6 }}>
                      {t}
                    </Tag>
                  ))}
                </Flex>

                <Flex gap={8} style={{ marginTop: 4 }}>
                  <Input autoComplete="off" placeholder="New tag name (e.g. VIP Client)..." value={newTagInput} onChange={(e) => setNewTagInput(e.target.value)} onPressEnter={handleAddTag} />
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddTag}>
                    Add Tag
                  </Button>
                </Flex>
              </Flex>
            </Card>
          </Col>

          {/* ── CARD 3: EMAIL INTEGRATION (RESEND) ── */}
          <Col xs={24} lg={12}>
            <Card className="org-card" styles={{ body: { padding: 20 } }}>
              <Flex vertical gap={14}>
                <Flex align="center" gap={8}>
                  <MailOutlined style={{ color: "#2459d3", fontSize: 18 }} />
                  <Title level={5} style={{ margin: 0, fontSize: "0.95rem" }}>
                    Email Gateway Integration (Resend)
                  </Title>
                </Flex>

                <div>
                  <Text style={{ fontSize: "0.85rem", fontWeight: 600 }}>Resend API Key</Text>
                  <Input.Password placeholder="re_..." value={resendApiKey} onChange={(e) => setResendApiKey(e.target.value)} style={{ marginTop: 4 }} />
                </div>

                <div>
                  <Text style={{ fontSize: "0.85rem", fontWeight: 600 }}>Sender Domain</Text>
                  <Input placeholder="support.yourcompany.com" value={resendSenderDomain} onChange={(e) => setResendSenderDomain(e.target.value)} style={{ marginTop: 4 }} />
                </div>
              </Flex>
            </Card>
          </Col>

          {/* ── CARD 4: COLLABORATING COMPANIES ── */}
          <Col xs={24} lg={12}>
            <Card className="org-card" styles={{ body: { padding: 20 } }}>
              <Flex vertical gap={14}>
                <Flex align="center" gap={8}>
                  <GlobalOutlined style={{ color: "#2459d3", fontSize: 18 }} />
                  <Title level={5} style={{ margin: 0, fontSize: "0.95rem" }}>
                    Collaborating Companies & Partners
                  </Title>
                </Flex>

                <Flex vertical gap={8}>
                  {collabs.length > 0 ? (
                    collabs.map((c, idx) => {
                      const code = typeof c === "string" ? c : c.companyCode || c.companyName;
                      const name = typeof c === "string" ? c : c.companyName || c.companyCode;
                      const uniqueKey = code || name || `collab-${idx}`;
                      const isPending = typeof c !== "string" && c.status === "pending";
                      return (
                        <Flex
                          key={uniqueKey}
                          justify="space-between"
                          align="center"
                          style={{
                            padding: "8px 12px",
                            background: "#f8fafc",
                            borderRadius: 8,
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <Text strong style={{ fontSize: "0.85rem", color: "#0f172a" }}>
                            {name}
                          </Text>
                          <Flex align="center" gap={8}>
                            <Tag color={isPending ? "orange" : "green"} style={{ margin: 0 }}>
                              {isPending ? "Requested" : "Connected"}
                            </Tag>
                            <Popconfirm title="Remove Partner" description={`Are you sure you want to remove partner "${code}"?`} onConfirm={() => handleRemoveCollab(code)} okText="Yes, Remove" cancelText="No" okButtonProps={{ danger: true }}>
                              <Tooltip title="Remove Partner">
                                <Button type="text" size="small" icon={<DeleteOutlined style={{ color: "#ef4444" }} />} />
                              </Tooltip>
                            </Popconfirm>
                          </Flex>
                        </Flex>
                      );
                    })
                  ) : (
                    <Text type="secondary" style={{ fontSize: "0.8rem" }}>
                      No partner companies linked yet.
                    </Text>
                  )}
                </Flex>

                <Flex gap={8} style={{ marginTop: 4 }}>
                  <Input placeholder="Enter Partner Company Code (e.g. STP-1603-2026)..." value={newCollabInput} onChange={(e) => setNewCollabInput(e.target.value)} onPressEnter={handleAddCollab} />
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddCollab} style={{ background: "#2563eb", fontWeight: 600 }}>
                    Add Partner
                  </Button>
                </Flex>
              </Flex>
            </Card>
          </Col>
        </Row>
      </Flex>
    </Spin>
  );
}

export default AppSettings;
